import { ENTITY_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
import { ref, Ref, watch } from 'vue';
import { inputField, tagField } from '@/ui/components/form';
import { Task, useValidation } from '@/ui/concerns';
import { User, validate } from '@/entities/user';
import { apiErrorMessage, upgradeMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { dropdownField } from '@/ui/components/form/DropdownField';
import { SelectOptions } from '@/ui/types';
import { mergedRoleOptions } from '@/ui/pages/user/options';
import { hasFeature } from '@/ui/auth/auth';
import { chooseEntityFolderField } from '@/ui/components/entityFolder/ChooseEntityFolderField';
import { permissionsDropdown } from '@/ui/components/permission/PermissionsDropdown';

export function useUserEditForm(record: Ref<User>, task: Task) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record, task.state);

  const handleSubmit = () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      task.run();
    }
  };

  const originalUser = ref<null | User>(null);

  const onRecordUpdate = (newRecord: User) => {
    if (originalUser.value === null) {
      // for whatever reason when assigning `newRecord` to `originalUser.value`
      // it keeps the reference and not the value itself
      originalUser.value = JSON.parse(JSON.stringify(newRecord));
    }
  };

  const rolesAndPermissions = (roleOptions: SelectOptions) => [
    permissionsDropdown<string>(
      {
        name: 'permissions',
        label: t('common.permissions'),
        value: record.value.permissions,
        placeholder: t('user.user-to-permissions'),
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },
      value => {
        record.value.permissions = value;
      },
    ),

    dropdownField(
      {
        name: 'role',
        label: t('common.role'),
        value: record.value.role,
        placeholder: t('form.select-role'),
        required: false,
        options: mergedRoleOptions(roleOptions),
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.role = value;
      },
    ),
  ];

  const formFields = (
    entityItemOptions: SelectOptions,
    groupOptions: SelectOptions,
    roleOptions: SelectOptions,
    entityFolderOptions: SelectOptions,
  ) => [
    inputField(
      {
        name: 'name',
        label: t('common.name'),
        placeholder: t('user.form.enter-name'),
        value: record.value.name,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.name = value;
      },
    ),

    inputField(
      {
        name: 'email',
        label: t('common.email'),
        placeholder: t('user.form.enter-email'),
        value: record.value.email,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.email = value;
      },
    ),

    ...(hasFeature('additionalNotificationChannels')
      ? rolesAndPermissions(roleOptions)
      : [upgradeMessage(t('message.need-roles-and-permissions'))]),

    entityItemOptions.length > 0 ? tagField(
      {
        name: 'entityItems',
        label: t('common.entity-items'),
        value: record.value.entityItems,
        placeholder: t('user.user-to-entity-item'),
        required: false,
        options: entityItemOptions,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        maxListedOptions: ENTITY_ITEMS_DROPDOWN_LIMIT,
      },

      value => {
        record.value.entityItems = value;
      },
    ) : null,

    entityFolderOptions.length > 0 ? chooseEntityFolderField({
      props: {
        value: record.value.entityFolders,
        options: entityFolderOptions,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        required: false,
        placeholder: t('user.user-to-entity-folder'),
        maxListedOptions: ENTITY_ITEMS_DROPDOWN_LIMIT,
      },
      on: {
        input: (value: number[]) => {
          record.value.entityFolders = value;
        },
      },
    }) : null,

    tagField(
      {
        name: 'groups',
        label: t('common.groups'),
        value: record.value.groups,
        placeholder: t('user.user-to-group'),
        required: false,
        options: groupOptions,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.groups = value;
      },
    ),

    apiErrorMessage(task.state.value, {
      'only.admin.can.remove.admin': t('error.cant-remove-administrator-role-with-manage-user-permission'),
      'admin.cannot.remove.from.himself': t('error.cant-remove-administrator-role-from-himself'),
      'only.one.admin.left': t('error.only-administrator'),
    }),
  ];

  watch(record, onRecordUpdate);

  return {
    formFields,
    handleSubmit,
  };
}
